/**
 * Dock Utilization Component
 * Shows real-time dock occupancy and status
 */
import { Layout, Circle, Loader } from 'lucide-react';

const DockUtilization = ({ data, isFullScreen }) => {
  if (!data) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
          <Layout className="w-6 h-6 mr-2 text-blue-600" />
          Dock Utilization
        </h2>
        <div className="text-center py-8 text-gray-500">
          <Loader className="w-8 h-8 animate-spin mx-auto mb-2" />
          <p>Loading dock status...</p>
        </div>
      </div>
    );
  }

  const docks = data.docks || [];
  const stats = data.stats || { total: 0, occupied: 0, available: 0, utilization_rate: 0 };

  const getDockStatusColor = (status) => {
    const colors = {
      OCCUPIED: 'bg-blue-500',
      AVAILABLE: 'bg-green-500',
      MAINTENANCE: 'bg-yellow-500',
      BLOCKED: 'bg-red-500',
    };
    return colors[status] || 'bg-gray-400';
  };

  const getDockStatusText = (status) => {
    const texts = {
      OCCUPIED: 'Occupied',
      AVAILABLE: 'Available',
      MAINTENANCE: 'Maintenance',
      BLOCKED: 'Blocked',
    };
    return texts[status] || 'Unknown';
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className={`font-bold text-gray-800 flex items-center ${isFullScreen ? 'text-lg' : 'text-xl'}`}>
          <Layout className="w-6 h-6 mr-2 text-blue-600" />
          Dock Utilization
        </h2>
        <div className="text-right">
          <div className="text-3xl font-bold text-blue-600">
            {Math.round(stats.utilization_rate || 0)}%
          </div>
          <div className="text-sm text-gray-600">
            {stats.occupied}/{stats.total} Docks
          </div>
        </div>
      </div>

      {/* Utilization Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-blue-50 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-blue-600">{stats.occupied || 0}</div>
          <div className="text-sm text-gray-600">Occupied</div>
        </div>
        <div className="bg-green-50 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-green-600">{stats.available || 0}</div>
          <div className="text-sm text-gray-600">Available</div>
        </div>
        <div className="bg-purple-50 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-purple-600">{stats.total || 0}</div>
          <div className="text-sm text-gray-600">Total</div>
        </div>
      </div>

      {/* Dock Grid */}
      {docks.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p>No dock data available</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {docks.map((dock, index) => {
            const statusColor = getDockStatusColor(dock.status);
            const statusText = getDockStatusText(dock.status);

            return (
              <div
                key={dock.dock_id || index}
                className={`relative rounded-lg p-4 border-2 transition-all hover:shadow-lg ${
                  dock.status === 'OCCUPIED'
                    ? 'border-blue-400 bg-blue-50'
                    : dock.status === 'AVAILABLE'
                    ? 'border-green-400 bg-green-50'
                    : 'border-gray-300 bg-gray-50'
                }`}
              >
                {/* Dock Number */}
                <div className="text-center mb-2">
                  <div className="text-lg font-bold text-gray-800">
                    {dock.dock_name || `Dock ${dock.dock_id}`}
                  </div>
                </div>

                {/* Status Indicator */}
                <div className="flex items-center justify-center mb-2">
                  <Circle className={`w-8 h-8 ${statusColor} rounded-full`} fill="currentColor" />
                </div>

                {/* Status Text */}
                <div className="text-center text-xs font-semibold text-gray-700">
                  {statusText}
                </div>

                {/* Truck Info (if occupied) */}
                {dock.status === 'OCCUPIED' && dock.truck_number && (
                  <div className="mt-2 text-center">
                    <div className="text-xs text-gray-600 truncate" title={dock.truck_number}>
                      {dock.truck_number}
                    </div>
                    {dock.duration && (
                      <div className="text-xs text-gray-500">
                        {Math.floor(dock.duration / 60)}m
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Legend */}
      <div className="mt-6 flex items-center justify-center space-x-6 text-sm">
        <div className="flex items-center space-x-2">
          <Circle className="w-4 h-4 bg-blue-500 rounded-full" fill="currentColor" />
          <span className="text-gray-600">Occupied</span>
        </div>
        <div className="flex items-center space-x-2">
          <Circle className="w-4 h-4 bg-green-500 rounded-full" fill="currentColor" />
          <span className="text-gray-600">Available</span>
        </div>
        <div className="flex items-center space-x-2">
          <Circle className="w-4 h-4 bg-yellow-500 rounded-full" fill="currentColor" />
          <span className="text-gray-600">Maintenance</span>
        </div>
        <div className="flex items-center space-x-2">
          <Circle className="w-4 h-4 bg-red-500 rounded-full" fill="currentColor" />
          <span className="text-gray-600">Blocked</span>
        </div>
      </div>
    </div>
  );
};

export default DockUtilization;
